<template>
  <div>
    <div v-if="!product || !product.variants || product.variants.length === 0" class="tw-text-gray-500 tw-text-center">
      {{ $t('common.no_product_variants') }}
    </div>
    
    <div v-else>
      <div class="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200">
        <table class="tw-w-full tw-border-collapse tw-table-auto">
          <thead class="tw-bg-gray-50">
            <tr>
              <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                {{ $t('fields.variant_name') }}
              </th>
              <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-w-24">
                {{ $t('fields.available') }}
              </th>
              <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-w-24">
                {{ $t('fields.quantity') }}
              </th>
              <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-w-24">
                {{ $t('fields.actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="variant in product.variants" :key="variant.id" 
                :class="[isVariantSelected(variant.id) ? 'tw-bg-primary-50' : 'tw-hover:bg-gray-50']">
              <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                <span :class="{'tw-font-medium': isVariantSelected(variant.id)}">{{ variant.variant_name }}</span>
              </td>
              <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">{{ variant.quantity }}</td>
              <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                <FormField 
                  v-model.number="selectedVariantsQuantity[variant.id]" 
                  type="number" 
                  hideLabel 
                  hideErrors
                  :disabled="!isVariantSelected(variant.id)"
                  class="tw-w-full quantity-input"
                />
                <div v-if="quantityErrors[variant.id]" class="tw-text-red-500 tw-text-xs">
                  {{ $t('errors.quantity_required') }}
                </div>
              </td>
              <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-center">
                <a-button
                  v-if="isVariantSelected(variant.id)"
                  @click="toggleVariant(variant)"
                  icon="heroicons:minus-circle-16-solid"
                  intent="danger"
                  size="sm"
                  class="tw-text-xs"
                >
                </a-button>
                <a-button
                  v-else
                  @click="toggleVariant(variant)"
                  icon="heroicons:plus-16-solid"
                  intent="primary"
                  size="sm"
                  class="tw-text-xs"
                >
                </a-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div v-if="!hasSelectedVariants" class="tw-text-amber-600 tw-text-xs tw-mt-1">
        {{ $t('messages.select_at_least_one_variant') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, computed } from 'vue';
import FormField from "@/components/form/FormField";

const props = defineProps({
  product: {
    type: Object,
    default: null
  },
  selectedVariants: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:selectedVariants']);

const selectedVariantsQuantity = ref({});
const selectedVariantsMap = ref({});
const quantityErrors = ref({});

// Compute if we have any selected variants
const hasSelectedVariants = computed(() => {
  return props.selectedVariants && props.selectedVariants.length > 0;
});

// Initialize the selected variants map for easy access
onMounted(() => {
  // Call after the component is fully mounted to avoid reactivity issues
  setTimeout(() => {
    initializeSelections();
  }, 0);
});

const initializeSelections = () => {
  if (!props.selectedVariants || props.selectedVariants.length === 0) {
    selectedVariantsMap.value = {};
    selectedVariantsQuantity.value = {};
    return;
  }
  
  // Create temporary objects first
  const tempMap = {};
  const tempQuantities = {};
  
  props.selectedVariants.forEach(v => {
    if (v && v.variant_id) {
      tempMap[v.variant_id] = true;
      tempQuantities[v.variant_id] = v.quantity || 1;
    }
  });
  
  // Then assign them all at once
  selectedVariantsMap.value = tempMap;
  selectedVariantsQuantity.value = tempQuantities;
};

// Check if a variant is selected
const isVariantSelected = (variantId) => {
  return !!selectedVariantsMap.value[variantId];
};

// Validate a variant's quantity
const validateQuantity = (variantId) => {
  const quantity = selectedVariantsQuantity.value[variantId];
  const isValid = quantity !== undefined && quantity > 0;
  quantityErrors.value[variantId] = !isValid;
  return isValid;
};

// Toggle selection of a variant
const toggleVariant = (variant) => {
  const variantId = variant.id;
  
  if (isVariantSelected(variantId)) {
    // Create a new array first to avoid reactive dependency issues
    const updatedVariants = props.selectedVariants.filter(v => v.variant_id !== variantId);
    
    // Only after preparing the new array, update the local state
    selectedVariantsMap.value = { ...selectedVariantsMap.value, [variantId]: false };
    quantityErrors.value = { ...quantityErrors.value, [variantId]: false };
    
    // Then emit the event
    emit('update:selectedVariants', updatedVariants);
  } else {
    // Prepare the new variant to add
    const newVariant = {
      variant_id: variantId,
      variant_name: variant.variant_name,
      quantity: 1,
      product_id: variant.product_id
    };
    
    // Create a new array with the added variant
    const updatedVariants = [...props.selectedVariants, newVariant];
    
    // Update the local state
    selectedVariantsMap.value = { ...selectedVariantsMap.value, [variantId]: true };
    selectedVariantsQuantity.value = { ...selectedVariantsQuantity.value, [variantId]: 1 };
    quantityErrors.value = { ...quantityErrors.value, [variantId]: false };
    
    // Then emit the event
    emit('update:selectedVariants', updatedVariants);
  }
};

// Watch for changes in quantity to update the selected variants
watch(selectedVariantsQuantity, (newQuantities) => {
  // First validate all quantities
  let hasErrors = false;
  Object.keys(selectedVariantsMap.value)
    .filter(variantId => selectedVariantsMap.value[variantId])
    .forEach(variantId => {
      if (!validateQuantity(variantId)) {
        hasErrors = true;
      }
    });
  
  if (hasErrors) return;
  
  // Create new array without triggering the other watcher
  const updatedVariants = Object.keys(selectedVariantsMap.value)
    .filter(variantId => selectedVariantsMap.value[variantId])
    .map(variantId => {
      const variant = props.product.variants.find(v => v.id == variantId);
      if (!variant) return null;
      
      return {
        variant_id: parseInt(variantId),
        variant_name: variant.variant_name,
        quantity: newQuantities[variantId] || 0,
        product_id: variant.product_id
      };
    })
    .filter(Boolean); // Remove any null entries
  
  // Avoid unnecessary updates that could trigger recursive loops
  if (JSON.stringify(updatedVariants) !== JSON.stringify(props.selectedVariants)) {
    emit('update:selectedVariants', updatedVariants);
  }
}, { deep: true });

// Update when selectedVariants prop changes from outside
watch(() => props.selectedVariants, (newSelectedVariants, oldSelectedVariants) => {
  // Check if this is a redundant update
  if (JSON.stringify(newSelectedVariants) === JSON.stringify(oldSelectedVariants)) {
    return;
  }
  
  // Use temporary variables to update without triggering the other watcher
  const tempMap = {};
  const tempQuantities = {};
  
  newSelectedVariants.forEach(v => {
    tempMap[v.variant_id] = true;
    tempQuantities[v.variant_id] = v.quantity || 1;
  });
  
  // Assign all at once to avoid intermediate states
  selectedVariantsMap.value = tempMap;
  selectedVariantsQuantity.value = tempQuantities;
  quantityErrors.value = {};
}, { deep: true });

// Update when product changes
watch(() => props.product, () => {
  // Reset all state
  selectedVariantsMap.value = {};
  selectedVariantsQuantity.value = {};
  quantityErrors.value = {};
  initializeSelections();
});
</script>

<style>
.selected-row {
  background-color: rgba(var(--primary-500), 0.1);
}

.quantity-input input {
  background-color: white !important;
}
</style> 